import React, { useState } from "react";
import { type SocietyDashboardModuleViewWithDetails } from "shared/api/types/society/[societyId]/dashboards/[dashboardId]";
import { type AppMenuItem } from "shared/components/common/MenuComponent";
import { type PillarModalControl } from "shared/components/pillar-modal/usePillarModalControl";
import AdminSetWorkflowStateModal from "shared/components/workflow-action-modal/AdminSetWorkflowStateModal";
import WorkflowActionModalComponent from "shared/components/workflow-action-modal/WorkflowActionModalComponent";
import { useCustomRouter } from "shared/context/CustomRouterContext";
import { useCustomSession } from "shared/context/CustomSessionContext";
import { hasAdminPermission } from "shared/helpers/hasAdminPermission";
import { evaluateWorkflowAction } from "shared/helpers/workflow/evaluateWorkflowAction";
import { type WorkflowInstanceView } from "shared/mappers/database/workflow/workflow-instance";
import { SocietyAdminPermissions } from "shared/types/admin/permissions";
import {
  type WorkflowAction,
  WorkflowActionType,
  type WorkflowActionForm,
  type WorkflowActionModal,
  WorkflowActionRoles,
  type WorkflowActionHyperlink,
} from "shared/workflow/workflow-action";

export const useWorkflowInstancePossibleActions = ({
  workflowInstance,
  dashboardModulesData,
  refetchCallback,
  testid,
}: {
  workflowInstance?: WorkflowInstanceView;
  dashboardModulesData?: SocietyDashboardModuleViewWithDetails[];
  refetchCallback?: () => Promise<void>;
  testid: string;
}): {
  menuItems: AppMenuItem[];
  WorkflowInstanceModalComponents: () => React.ReactElement;
} => {
  const session = useCustomSession();
  const userHasRole = (workflowAction: WorkflowAction) => {
    if (session.adminMode) {
      return true;
    }
    return workflowAction.roles.some(
      (role) => role.toLowerCase() === WorkflowActionRoles.User.toLowerCase(),
    );
  };
  const router = useCustomRouter();

  const modalNames: string[] = [];

  const menuItems: AppMenuItem[] = [];
  const workflowModalComponents: React.ReactElement[] = [];

  const [openState, setOpenState] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(modalNames.map((id) => [id, false])),
  );

  const getControl = (modalId: string): PillarModalControl => ({
    isOpen: openState[modalId] ?? false,
    setIsOpen: (nextIsOpen) => {
      setOpenState((previousOpenState) => {
        const value =
          typeof nextIsOpen === "function"
            ? nextIsOpen(previousOpenState[modalId] ?? false)
            : nextIsOpen;
        return {
          ...previousOpenState,
          [modalId]: value,
        };
      });
    },
    onClose: async () => {
      setOpenState((prev) => ({ ...prev, [modalId]: false }));
    },
  });

  if (
    hasAdminPermission({
      session,
      permissions: [SocietyAdminPermissions.workflow_instance_change_state],
    })
  ) {
    modalNames.push("admin");
    menuItems.push({
      label: "Update Workflow State",
      onSelect: () => getControl("admin").setIsOpen(true),
    });
    workflowModalComponents.push(
      <AdminSetWorkflowStateModal
        key="admin"
        workflowInstance={workflowInstance!}
        control={getControl("admin")}
        testid={testid}
        refetchCallback={refetchCallback}
      />,
    );
  }
  if (workflowInstance?.possibleActions) {
    workflowInstance.possibleActions.forEach(
      (workflowAction: WorkflowAction) => {
        if (workflowAction.type) {
          if (
            userHasRole(workflowAction) &&
            [
              WorkflowActionType.FORM.toLowerCase(),
              WorkflowActionType.HYPERLINK.toLowerCase(),
            ].includes(workflowAction.type.toLowerCase())
          ) {
            const { isActionEnabled, itemClassName } = evaluateWorkflowAction({
              userHubEvaluation: workflowAction.userHubEvaluation,
              workflowInstance,
              session,
              dashboardModulesData,
              defaultEnabled: true,
              baseClassName: workflowAction.className,
            });
            menuItems.push({
              label: workflowAction.title,
              itemClassName,
              onSelect: () => {
                if (!isActionEnabled) {
                  return;
                }
                router.removeFromState("formId");
                router.removeFromState("workflowId");
                router.removeFromState("workflowInstanceId");
                router.removeFromState("invoiceId");

                const route =
                  (workflowAction as WorkflowActionForm).userhubFormRoute ??
                  (workflowAction as WorkflowActionHyperlink).userHubRoute;
                router.push(route, {
                  ...workflowAction,
                  societyId: session.societyId!,
                  profileId: session.profileId!,
                  formId:
                    workflowInstance?.workflowInstancePayload?.form?.formId ??
                    null,
                  workflowId: workflowInstance.workflow?.workflowId ?? null,
                  workflowInstanceId:
                    workflowInstance.workflowInstanceId ?? null,
                  invoiceId: workflowInstance.invoice?.[0]?.invoiceId ?? null,
                });
              },
            });
          } else if (
            userHasRole(workflowAction) &&
            workflowAction.type.toLowerCase() ===
              WorkflowActionType.MODAL.toLowerCase()
          ) {
            modalNames.push(workflowAction.event);
            menuItems.push({
              label: workflowAction.title,
              itemClassName: workflowAction.className,
              onSelect: () => getControl(workflowAction.event).setIsOpen(true),
            });
            workflowModalComponents.push(
              <WorkflowActionModalComponent
                key={workflowAction.event}
                workflowInstance={workflowInstance}
                modalAction={workflowAction as WorkflowActionModal}
                control={getControl(workflowAction.event)}
                testid={testid}
                refetchCallback={refetchCallback}
              />,
            );
          }
        }
      },
    );
  }

  return {
    menuItems,
    WorkflowInstanceModalComponents: () => <>{workflowModalComponents}</>,
  };
};
